<script setup lang="ts">
import { Loader2, Search, Users } from 'lucide-vue-next';
import { computed, ref, watch } from 'vue';

import ConnectionErrorState from '@/components/ConnectionErrorState.vue';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
    type AzureGroup,
    type AzureGroupMember,
    getAzureGroupMembers
} from '@/services/user_groups';
import {
    INVALID_SECRET_ADMIN_HINT,
    useMicrosoftConnectionStore
} from '@/stores/microsoftConnectionStore';

interface Props {
    group: AzureGroup;
    tenantId: string;
}

const props = defineProps<Props>();

const members = ref<AzureGroupMember[]>([]);
const loading = ref(false);
const error = ref<string | null>(null);
const searchQuery = ref('');

const microsoftConnectionStore = useMicrosoftConnectionStore();
const isSecretInvalid = computed(
    () => microsoftConnectionStore.health?.status === 'invalid_secret'
);

const filteredMembers = computed(() => {
    if (!searchQuery.value.trim()) {
        return members.value;
    }

    const query = searchQuery.value.toLowerCase();
    return members.value.filter((member) => {
        const nameMatch = member.displayName?.toLowerCase().includes(query);
        const emailMatch = member.mail?.toLowerCase().includes(query);
        const principalNameMatch = member.userPrincipalName
            ?.toLowerCase()
            .includes(query);
        return nameMatch || emailMatch || principalNameMatch;
    });
});

const loadMembers = async () => {
    loading.value = true;
    error.value = null;

    try {
        members.value = await getAzureGroupMembers(
            props.group.id,
            props.tenantId
        );
    } catch (err) {
        console.error('Error loading group members:', err);
        error.value = 'Failed to load members';
        // The backend does not name the cause; the health check tells a revoked secret apart.
        void microsoftConnectionStore.refresh();
    } finally {
        loading.value = false;
    }
};

watch(
    () => props.group,
    () => {
        searchQuery.value = '';
        loadMembers();
    },
    { immediate: true }
);
</script>

<template>
    <div class="flex flex-col h-full">
        <div class="flex-1 min-h-0 flex flex-col">
            <p class="font-semibold mb-3">
                Members
                <span v-if="!loading && !error">
                    ({{ filteredMembers.length }})
                </span>
            </p>

            <!-- Loading state -->
            <div v-if="loading" class="flex items-center justify-center h-32">
                <Loader2 class="w-6 h-6 animate-spin text-primary mr-2" />
                <span class="text-sm text-muted-foreground"
                    >Loading members...</span
                >
            </div>

            <!-- Error state -->
            <div
                v-else-if="error"
                class="flex flex-1 items-center justify-center px-8 pb-16"
            >
                <ConnectionErrorState
                    :title="
                        isSecretInvalid
                            ? 'Azure AD connection unavailable'
                            : 'Could not load members'
                    "
                    :detail="isSecretInvalid ? INVALID_SECRET_ADMIN_HINT : error"
                    :can-retry="!isSecretInvalid"
                    @retry="loadMembers"
                />
            </div>

            <!-- No members state -->
            <div
                v-else-if="members.length === 0"
                class="flex items-center justify-center h-32"
            >
                <div class="text-center">
                    <Users
                        class="w-12 h-12 mx-auto mb-3 text-muted-foreground/50"
                    />
                    <p class="text-sm text-muted-foreground">
                        No members found in this group
                    </p>
                </div>
            </div>

            <!-- Search and Members list -->
            <template v-else>
                <!-- Search Input -->
                <div class="mb-4 relative">
                    <Search
                        class="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground"
                    />
                    <Input
                        v-model="searchQuery"
                        type="text"
                        placeholder="Search members by name or email..."
                        class="pl-9"
                    />
                </div>

                <!-- No Search Results State -->
                <div
                    v-if="filteredMembers.length === 0"
                    class="flex items-center justify-center h-32"
                >
                    <div class="text-center">
                        <Search
                            class="w-12 h-12 mx-auto mb-3 text-muted-foreground"
                        />
                        <p class="text-sm text-muted-foreground">
                            No members match your search
                        </p>
                    </div>
                </div>

                <!-- Members list -->
                <ScrollArea v-else class="h-[55dvh] md:h-[60dvh]">
                    <div class="space-y-2 pr-2">
                        <div
                            v-for="member in filteredMembers"
                            :key="member.id"
                            class="flex items-center justify-between p-3 bg-card border rounded-lg"
                        >
                            <div class="flex-1 flex items-center gap-4">
                                <div class="flex-1">
                                    <p
                                        v-if="member.displayName"
                                        class="font-medium"
                                    >
                                        {{ member.displayName }}
                                    </p>
                                    <p v-else class="text-muted-foreground">
                                        Name not available
                                    </p>
                                </div>
                                <div class="flex-1">
                                    <p class="text-sm text-muted-foreground">
                                        {{
                                            member.mail ||
                                            member.userPrincipalName ||
                                            'N/A'
                                        }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </ScrollArea>
            </template>
        </div>
    </div>
</template>
